/**
 * Reactive dark-mode detector.
 *
 * Watches the `dark` class on <html> (set by ChatPage's toggleDark) and
 * re-renders consumers when it flips. Use this only for surfaces that
 * can't be themed via Tailwind `dark:` variants — e.g. third-party
 * libraries (recharts) that take literal color strings.
 */

import { useEffect, useState } from 'react';

function readDark(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
}

export function useIsDarkMode(): boolean {
  const [isDark, setIsDark] = useState<boolean>(readDark);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const target = document.documentElement;
    const update = () => setIsDark(target.classList.contains('dark'));
    update();
    const observer = new MutationObserver(update);
    observer.observe(target, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}
